/**
 * A one-way nudge from the verify tab to the setup tab.
 *
 * The CLI opens the verification page itself, so the setup tab has no way of knowing the command
 * ran: the device row only appears in the database *after* verification, and the CLI's device
 * session is anonymous until then. Both tabs are same-origin, so the verify tab can simply say
 * "I'm open" and the setup tab can move to its Verify card.
 *
 * BroadcastChannel where available, with a localStorage write as the fallback (the `storage` event
 * fires in other tabs of the same origin). If neither works the setup tab just stays on the Run
 * card, which is still a correct thing to be showing.
 */

const CHANNEL = "dc.remote.setup";
const STORAGE_PING = "dc.remote.setup.ping";

export type SetupSignal = "verify_opened";

export function announce(signal: SetupSignal): void {
    try {
        new BroadcastChannel(CHANNEL).postMessage(signal);
    } catch {
        // No BroadcastChannel — fall through to storage.
    }
    try {
        // Value must change for the storage event to fire in other tabs.
        window.localStorage.setItem(STORAGE_PING, `${signal}:${Date.now()}`);
    } catch {
        // Private mode or blocked storage; nothing to do.
    }
}

/** Calls back when another tab announces `signal`. Returns an unsubscribe function. */
export function listen(signal: SetupSignal, onSignal: () => void): () => void {
    let channel: BroadcastChannel | null = null;
    try {
        channel = new BroadcastChannel(CHANNEL);
        channel.onmessage = (event) => {
            if (event.data === signal) {
                onSignal();
            }
        };
    } catch {
        channel = null;
    }

    const onStorage = (event: StorageEvent) => {
        if (event.key === STORAGE_PING && event.newValue?.startsWith(signal)) {
            onSignal();
        }
    };
    window.addEventListener("storage", onStorage);

    return () => {
        channel?.close();
        window.removeEventListener("storage", onStorage);
    };
}
